import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Helmet } from "react-helmet";
import {
  Button,
  VStack,
  useToast,
  Icon,
} from "@chakra-ui/react";
import CommunityJobApplicationForm from "./community-job-application-form";
import { FaArrowLeft } from "react-icons/fa";
import { RequestFailed } from "src/components/RequestFailed";
import {Loading} from "src/components/Loading";
import { CREATE_COMMUNITY_JOB_APPLICATION, GET_COMMUNITY_JOB_OPPORTUNITY } from "src/features/communities/graphql";
import { logEvent } from "src/utlis/analytics";
import { JobApplicationFormData } from "src/features/jobs/constants";

export default function CommunityJobOpportunityApplication() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data, loading, error } = useQuery(GET_COMMUNITY_JOB_OPPORTUNITY, {
    variables: { id: id! },
    skip: !id,
  });

  const [createCommunityJobApplication, { loading: isCreatingApplication }] =
    useMutation(CREATE_COMMUNITY_JOB_APPLICATION, {
      onCompleted: (data) => {
        logEvent("community_job_application_submitted", {
          job_id: id,
          application_id: data.createCommunityJobApplication.id,
        });

        toast({
          title: "Application submitted",
          description:
            "Your community job application has been submitted successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        navigate(`/opportunities/communities/${id}`);
      },
      onError: (error) => {
        toast({
          title: "Application failed",
          description:
            error.message ||
            "Failed to submit your application. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });

  const handleSubmit = async (formData: JobApplicationFormData) => {
    try {
      const input = {
        jobOpportunityId: id!,
        educationItems: formData.educationItems,
        employmentHistoryItems: formData.employmentHistoryItems.map((item) => ({
          ...item,
          startedAt: convertToISOString(item.startedAt),
          endedAt: !!item.endedAt
            ? convertToISOString(item.endedAt)
            : undefined,
        })),
        projectHistoryItems: formData.projectHistoryItems.map((item) => ({
          ...item,
          startedAt: !!item.startedAt
            ? convertToISOString(item.startedAt)
            : undefined,
          endedAt: convertToISOString(item.endedAt),
        })),
        certificationItems: formData.certificationItems.map((item) => ({
          ...item,
          issuedAt: convertToISOString(item.issuedAt),
        })),
      };

      await createCommunityJobApplication({
        variables: { input },
      });
    } catch (error) {
      console.error("Community job application submission error:", error);
    }
  };

  const handleCancel = () => {
    navigate(`/opportunities/communities/${id}`);
  };

  useEffect(() => {
    if (data?.getCommunityJobOpportunityForPro) {
      logEvent("community_job_application_page_viewed", {
        job_id: id,
        job_title: data.getCommunityJobOpportunityForPro.title,
      });
    }
  }, [data, id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loading />
      </div>
    );
  }

  if (error || !data?.getCommunityJobOpportunityForPro) {
    return (
      <RequestFailed text={error?.message || "Job opportunity not found"} />
    );
  }

  const jobOpportunity = data.getCommunityJobOpportunityForPro;

  if (jobOpportunity.hasApplied && !jobOpportunity.canApply) {
    return (
      <>
        <Helmet>
          <title>
            Application Submitted - {jobOpportunity.title} - LaborHack Opportunities
          </title>
        </Helmet>
        <div>
          <h1>Application Submitted</h1>
          <p>You've already applied for this community job opportunity!</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Apply for {jobOpportunity.title} - LaborHack Opportunities</title>
      </Helmet>
      <VStack spacing={0} maxW={"99vw"} minH={"100vh"}>
        <section className="px-3 lg:px-20 w-full max-w-4xl">
          <div className="mb-8">
            <Button
              variant="link"
              colorScheme="prussianBlue"
              fontSize={{
                base: "14px",
                lg: "16px",
              }}
              onClick={handleCancel}
              className="mb-4 px-0"
              leftIcon={<Icon as={FaArrowLeft} w={4} h={4} />}
            >
              Back to job opportunity
            </Button>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0E212D] mb-2">
              Apply for {jobOpportunity.title}
            </h1>
            <p className="text-[#6E6E6E] text-lg">
              {jobOpportunity.organization.name} •{" "}
              {jobOpportunity.location.area}, {jobOpportunity.location.state}
            </p>
          </div>

          <CommunityJobApplicationForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isCreatingApplication}
            jobId={id!}
          />
        </section>
      </VStack>
    </>
  );
}

const convertToISOString = (dateString: string): string => {
  if (!dateString) return dateString;
  const date = new Date(dateString);
  return date.toISOString();
};
